import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export function Heading1({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { children: ReactNode }) {
  return (
    <h1 className={cn('scroll-m-20 text-h1 font-extrabold tracking-tight', className)} {...props}>
      {children}
    </h1>
  );
}

export function Heading2({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { children: ReactNode }) {
  return (
    <h2 className={cn('scroll-m-20 text-h2 font-bold tracking-tight', className)} {...props}>
      {children}
    </h2>
  );
}

export function Heading3({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { children: ReactNode }) {
  return (
    <h3 className={cn('scroll-m-20 text-h3 font-semibold tracking-tight', className)} {...props}>
      {children}
    </h3>
  );
}

export function Heading4({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { children: ReactNode }) {
  return (
    <h4 className={cn('scroll-m-20 text-h4 font-semibold tracking-tight', className)} {...props}>
      {children}
    </h4>
  );
}

export function Heading5({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { children: ReactNode }) {
  return (
    <h5 className={cn('scroll-m-20 text-h5 font-semibold tracking-tight', className)} {...props}>
      {children}
    </h5>
  );
}

export function Paragraph({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { children: ReactNode }) {
  return (
    <p className={cn('leading-4', className)} {...props}>
      {children}
    </p>
  );
}

export function Blockquote({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLQuoteElement> & { children: ReactNode }) {
  return (
    <blockquote className={cn('border-l-2 pl-6 italic', className)} {...props}>
      {children}
    </blockquote>
  );
}

export function UnorderedList({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLUListElement> & { children: ReactNode }) {
  return (
    <ul className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)} {...props}>
      {children}
    </ul>
  );
}

export function OrderedList({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLOListElement> & { children: ReactNode }) {
  return (
    <ol className={cn('my-6 ml-6 list-decimal [&>li]:mt-2', className)} {...props}>
      {children}
    </ol>
  );
}

export function LeadParagraph({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { children: ReactNode }) {
  return (
    <p className={cn('text-xl', className)} {...props}>
      {children}
    </p>
  );
}

export function SmallText({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { children: ReactNode }) {
  return (
    <p className={cn('text-sm leading-none', className)} {...props}>
      {children}
    </p>
  );
}

export function ExtraSmallText({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { children: ReactNode }) {
  return (
    <p className={cn('text-xs leading-none', className)} {...props}>
      {children}
    </p>
  );
}

export function MutedText({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { children: ReactNode }) {
  return (
    <p className={cn('text-sm text-muted-foreground', className)} {...props}>
      {children}
    </p>
  );
}

export function DestructiveText({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { children: ReactNode }) {
  return (
    <p className={cn('text-sm text-destructive', className)} {...props}>
      {children}
    </p>
  );
}
